import React, { useState, useEffect, useMemo } from 'react'
import {connect, useSelector} from 'react-redux';
import {paymentPreviewSelect} from '../../redux/selector/paymentPreviewSelector'
import { 
    Button, 
    ErrorMark, 
    Header, 
    NavigationLinkButton 
} from '../../components/RegularComponents'
import { fetchApplicationReferenceData, saveSegueDetails } from '../../redux/slice/primaryAgencyApplicationSlice';
import { computeMLCost, financialNumber, replaceUrlParams } from '../../app/utils';
import { 
    APPLICATION_MODE_NEW,
    LICENCE_SUB_TYPE_MORTGAGE_LENDER_NEW_APPLICATION,
    APPLICATION_TYPE_MORTGAGE_LENDER_LICENCE, 
    PAYMENT_STATUS_FAILURE, 
    PAYMENT_STATUS_INPROGRESS, 
    ROUTE_PATH, 
    SEGUE_NAME
} from '../../app/constants';
import { ERROR_STRING } from '../../app/errors';
import { fetchApplicationWithIdAndSetStore, initiatePayment_v1 } from '../../redux/slice/collectionApplicationSlice';
import { saveBusinessLicenceApplicationData } from '../../redux/slice/businessLicenceApplicationSlice';
import { push } from 'connected-react-router';
import { applicationTypeFromLicenceSubType } from '../../redux/slice/applicationsUtils';

const MLPaymentPreview = (props) => {
    const { paymentPreview,dispatch } = props
    const { feesStructure, paymentStatus } = paymentPreview

    const { licencingSubType } = useSelector((state) => ({
        licencingSubType : state.primaryAgencyApplication.application?.licencingSubType ?? LICENCE_SUB_TYPE_MORTGAGE_LENDER_NEW_APPLICATION,
    }));

    const applicationType = applicationTypeFromLicenceSubType(licencingSubType);
    const applicationMode = APPLICATION_MODE_NEW;
    const isLender = applicationType === APPLICATION_TYPE_MORTGAGE_LENDER_LICENCE;

    const { primaryApplication, referenceData } = paymentPreview

    const [fetchingFeesData, setFetchingFeesData] = useState(false)
    const [attemptedFetchingPrimaryApplications, setAttemptedFetchingPrimaryApplications] = useState(false)
    const [fetchingApplicationData, setFetchingApplicationData] = useState(false)

    const [helpPanelOpen, setHelpPanelOpen] = useState(false)
    const [acceptedCardHelpPanelOpen, setAcceptedCardHelpPanelOpen] = useState(false)

    /* 
     * Fetch the fees details, if not fetched already
     */
    useEffect(() => {
        if (!referenceData
            && !fetchingFeesData) {
            setFetchingFeesData(true)
            dispatch(fetchApplicationReferenceData())
                    .then(() => setFetchingFeesData(false))
        }
    },[referenceData, fetchingFeesData, dispatch])

    useEffect(() => {

        if(attemptedFetchingPrimaryApplications) {
            // console.log("primary agency application data already fetched")
            return
        }
        
        if (primaryApplication && !primaryApplication.businessLicenceData) { 
            // console.log("had to fetch primary agency application data")
            setFetchingApplicationData(true)
            setAttemptedFetchingPrimaryApplications(true)

            dispatch(fetchApplicationWithIdAndSetStore(primaryApplication.id))
                .then(data => {
                    // no need to do anything
                })
                .finally(() => {
                    setFetchingApplicationData(false)
                })
        }


    }, [
        primaryApplication,
        attemptedFetchingPrimaryApplications,
        setAttemptedFetchingPrimaryApplications,
        setFetchingApplicationData,
        dispatch
    ])

    const costBreakup = useMemo(() => {
        // lenderFees is part of mortgage-brokerage-licence key. 
        // This is parsed in the paymentPreviewSelector.js file
        const agencyFeesStructure = feesStructure["mortgage-brokerage-licence"]
        
        if(!agencyFeesStructure) {
            return {}
        }

        const { lenderFees } = agencyFeesStructure
        return computeMLCost(primaryApplication,
            lenderFees)
    }, [primaryApplication, feesStructure])

    const totalAmountToPay = useMemo(() => {
        const { lenderCost } = costBreakup
        return financialNumber(lenderCost)
    }, [costBreakup])


    // UI Components 
    const costBreakupTable = () => {
        if(feesStructure 
            && feesStructure["mortgage-brokerage-licence"]
            && primaryApplication) {
            let agencyFeesStructure = feesStructure["mortgage-brokerage-licence"]
    
            return (
                <div className="row">
                    <div className="col-lg-8">
                        <table className="table mraCostBreakupTable">
                            <thead>
                                <tr>
                                    <th>Description</th>
                                    <th>Quantity</th>
                                    <th>Cost</th>
                                    <th>Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {costBreakup.lenderCost ? <tr>
                                    <td>{isLender ? 'Mortgage Lender Licence' : 'Mortgage Administrator Licence'}</td>
                                    <td>1</td>
                                    <td>{`$${financialNumber(agencyFeesStructure.lenderFees)} CDN`}</td>
                                    <td>{`$${financialNumber(costBreakup.lenderCost)} CDN`}</td>
                                </tr> : null }
                                
                                <tr>
                                    <th colSpan="3">
                                        Total Cost
                                    </th>
                                    <td><strong>{`$${totalAmountToPay}`}</strong>{` CDN`}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            )
        }
        
        return null
    }

    const paymentStaticText = () => {

        return (<div id="ml-payment-status-text">
                
                <div className="row bottomMarginMedium">
                    <div className="col-lg-8">
                        <span style={{fontSize: "2em"}}>
                            Choosing the right payment method
                        </span>
                    </div>
                </div>

                <div className="row bottomMarginMedium">
                    <div className="col-lg-8">
                        <h2>Paying with the credit or bank card</h2>
                    </div>

                    <div className="col-lg-8 bottomMarginNormal">
                        <span><strong>You can select to pay with a credit or debit card.</strong> We accept most debit cards with a Visa or Mastercard logo, as well as all major credit cards.</span>
                    </div>

                    <div 
                        className="col-lg-8" 
                        style={{padding: "15px", borderTop: "1px solid #777",borderBottom: "1px solid #777", cursor: "pointer"}}
                        onClick={() => {
                            setAcceptedCardHelpPanelOpen(!acceptedCardHelpPanelOpen)
                        }}
                    >
                        <div style={{color: "#0058a4"}}>
                            <strong>Which cards are accepted?</strong>
                            {acceptedCardHelpPanelOpen ? 
                                <i className="fa fa-lg fa-minus pull-right" /> : <i className="fa fa-lg fa-plus pull-right" />}
                        </div>
                        {acceptedCardHelpPanelOpen && 
                        <div style={{padding: "15px 0px"}}>
                            <strong>Using a debit card:&nbsp;</strong> If your debit card has a Visa or MasterCard logo on it try entering it using the credit, debit-credit option.
                            Get information on &nbsp;
                            <a 
                                style={{textDecoration: "underline"}} 
                                href="https://www.visa.ca/en_CA/pay-with-visa/cards/debit-cards.html" 
                                target="_blank" 
                                rel="noreferrer">
                                    Visa Debit as visa.ca
                            </a>
                            &nbsp;and&nbsp;
                            <a 
                                style={{textDecoration: "underline"}} 
                                href="https://www.mastercard.ca/en-ca/personal/find-a-card/debit-cards.html" 
                                target="_blank" 
                                rel="noreferrer">
                                    Debit MasterCard at mastercard.ca
                            </a>
                            <br /><br />
                            <strong>Using a credit card:&nbsp;</strong> 
                            Visa, MasterCard, and American Express
                        </div>
                        }
                    </div>

                    <div className="col-lg-8">
                        <h2>Paying from your bank account with Interac Online &reg;</h2>
                    </div>

                    <div className="col-lg-8 bottomMarginNormal">
                        <span><strong>Selecting to pay with Interac Online &reg;</strong>
                        &nbsp; will take you to online banking website. You'll be able to review and submit your payment after you login to your online banking account.</span>
                    </div>

                    <div 
                        className="col-lg-8"
                        style={{
                            padding: "15px", 
                            borderTop: "1px solid #777",
                            borderBottom: "1px solid #777",
                            font: "1.2em",
                            color : " #e69304"
                        }}
                    >
                        <i className="fa fa-2x fa-exclamation-triangle" />
                        &nbsp; 
                        <strong>Not all banks support payments through Interac Online &reg;</strong>
                    </div>

                </div>


                <div className="row bottomMarginLarge">
                    <div 
                        className="col-lg-8" 
                        style={{
                            paddingBottom: "15px", 
                            borderBottom: "1px solid #777", 
                            cursor: "pointer"}}
                        onClick={() => {
                            setHelpPanelOpen(!helpPanelOpen)
                        }}
                    >
                        <div style={{color: "#0058a4"}}>
                            <strong>Does my bank support Interac Online&reg; ?</strong>
                            {helpPanelOpen ? <i className="fa fa-lg fa-minus pull-right" /> : <i className="fa fa-lg fa-plus pull-right" />}
                        </div>
                        {helpPanelOpen && 
                        <div style={{padding: "15px 0px"}}>
                            <strong>Interac Online &reg; is provided by Interac Inc. in partnership with participating banks.</strong>
                            <br />
                            Not all banks choose to participate in this service. Review &nbsp;
                            <a 
                                style={{textDecoration: "underline"}} 
                                href="https://www.interac.ca/en/consumers/products/interac-debit/e-commerce/#fi-list-interac-online"
                                target="_blank" 
                                rel="noreferrer">
                                the list of participating banks
                            </a> to see if your bank is one of them.
                        </div>
                        }
                    </div>
                </div>

            </div>)
    }

    // Button handlers
    const onContinue = () => {
        dispatch(saveBusinessLicenceApplicationData(applicationType, applicationMode))
        .then(data => {
            dispatch(initiatePayment_v1(applicationType,applicationMode)).then(() => {
                dispatch(saveSegueDetails({
                    name : SEGUE_NAME.PAYMENT,
                    applicationType,
                    applicationMode
                }))
            })
        })
    }

    const onCancel = () => {
        // go back to the welcome screen
        const nextUrl = replaceUrlParams(ROUTE_PATH.WELCOME, 
            applicationType, applicationMode)
        dispatch(push(nextUrl));
    }

    if(fetchingFeesData || fetchingApplicationData) {
        return (
            <div className={"container"}>
                <div className="row topMarginLarge">
                    <div className="col-12 centered">
                        <p className="statusText">
                            ...
                        </p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="container">
            <div className="row bottomMarginLarge">
                <div className="col-lg-8">
                    <Header 
                        id="ml-payment-preview-header"
                        header="Get ready to pay"
                    />
                </div>
            </div>

            <div className="row bottomMarginNormal">
                <div className="col-lg-8">
                    <span style={{fontSize: "2em"}}>
                        Amount to pay : $
                        <span style={{fontWeight: "bold"}}>{totalAmountToPay}</span>
                    </span>
                    <span style={{fontSize: "1.5em"}}>{" CDN"}</span>
                </div>
            </div>

            {costBreakupTable()}

            {paymentStaticText()}

            <div className="row">
                <div className="col-lg-2">
                    <div className={(paymentStatus === PAYMENT_STATUS_FAILURE) ? " has-error" : ""}>
                        <Button
                            id="ml-payment-preview-continue-button" 
                            onClick = {onContinue}
                            text={"Continue"} 
                            showSpinner={paymentStatus === PAYMENT_STATUS_INPROGRESS}
                            buttonType={paymentStatus === PAYMENT_STATUS_INPROGRESS ? "regular_disabled" : "regular_primary"}
                        />
                        {(paymentStatus === PAYMENT_STATUS_FAILURE) && ErrorMark()}
                    </div>
                </div>
                <div className="col-lg-4">
                    <NavigationLinkButton 
                        id="ml-payment-preview-cancel-button"
                        text="Cancel"
                        onClick={onCancel}
                    />
                </div>

                {(paymentStatus === PAYMENT_STATUS_FAILURE) && (
                    <div className="col-lg-8 ns-error-message topMarginNormal">
                        <span className="ns-visually-hidden">Error:</span>
                        {ERROR_STRING.PAYMENT_PREVIEW_PAYMENT_FAIL}
                    </div>
                )}
            </div>
        </div>
    )
}

export default connect(paymentPreviewSelect) (MLPaymentPreview)